import React from 'react';

/** data URL / голый base64 для аватаров и превью */
export function ensureDataUrl(val) {
  if (!val || typeof val !== 'string') return null;
  if (val.startsWith('data:')) return val;
  const looksB64 = /[A-Za-z0-9+/=]{60,}/.test(val);
  if (looksB64) return `data:image/jpeg;base64,${val}`;
  return val;
}

/** Ответ сокета get_profile / update_profile: avatar + avatar_mime → avatarDataUrl для UI */
export function normalizeProfileFromServer(profile) {
  if (!profile || typeof profile !== 'object') return profile;
  const out = { ...profile };
  if (!out.avatarDataUrl && typeof out.avatar === 'string' && out.avatar.length > 0) {
    const mime =
      typeof out.avatar_mime === 'string' && out.avatar_mime.trim() ? out.avatar_mime.trim() : 'image/jpeg';
    out.avatarDataUrl = `data:${mime};base64,${out.avatar}`;
  }
  return out;
}

/** Base64 изображения для карточек предложений (бренд / аналог) */
export function getImageSrc(img) {
  if (typeof img !== 'string' || !img) return '';
  return img.startsWith('data:image') ? img : `data:image/jpeg;base64,${img}`;
}

/** Универсальная проверка «пустого» значения для модалок документов и WIPO */
export function isEmpty(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'boolean' && value === false) return true;
  if (typeof value === 'string' && (value.trim() === '' || value.trim() === 'Нет')) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return true;
  }
  return false;
}

/**
 * Читаемый статус ТЗ/заявки: состояние делопроизводства важнее полей «Статус» / StatusText.
 */
export function getDocumentStatusDisplay(doc = {}) {
  const delo = doc['Состояние_делопроизводства'];
  if (delo != null && String(delo).trim() !== '') return String(delo).trim();
  const st = doc.StatusText ?? doc['Статус'] ?? doc.Status;
  if (st != null && String(st).trim() !== '') return String(st).trim();
  return '';
}

/** Значение поля для WIPO-модалки (один аргумент) */
export function renderPlainFieldValue(value) {
  if (Array.isArray(value)) {
    return (
      <ul style={{ paddingLeft: 16, margin: 0 }}>
        {value.map((item, idx) => (
          <li key={idx} style={{ listStyle: 'disc', marginBottom: 2 }}>{String(item)}</li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'string' && /^https?:\/\//.test(value)) {
    return <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>;
  }
  if (typeof value === 'object' && value) {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/** Элемент списка полей WIPO */
export function renderWipoFieldItem(label, value) {
  if (isEmpty(value)) return null;
  return (
    <div style={{ marginBottom: 8, padding: '4px 0' }}>
      <b style={{ color: '#495057' }}>{label}:</b> {renderPlainFieldValue(value)}
    </div>
  );
}

/** Извлечение классов МКТУ для данных WIPO */
export function extractMktu(data) {
  const nic = data?.['Nice Cl.'] || data?.['MARKGR/CURRENT/BASICGS@NICEVER'] || '';
  const rawText = data?.['MARKGR/CURRENT/BASICGS'] || '';
  const nicNumbers = String(nic).match(/\d+/g) || [];
  const uniqueNumbers = [...new Set(nicNumbers)];
  return { numbers: uniqueNumbers, fullText: rawText };
}

/** Парсинг поля классов МКТУ (Роспатент / заявки) */
export function processMktuClasses(mktuData) {
  if (!mktuData) return { numbers: [], fullText: mktuData };
  const mktuString = String(mktuData);
  const numbers = mktuString.match(/\d+/g) || [];
  const uniqueNumbers = [...new Set(numbers)].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  return {
    numbers: uniqueNumbers,
    fullText: mktuData,
  };
}

/** Значение поля для DocumentModal (с учётом url_ и булевых) */
export function renderDocumentFieldValue(key, value) {
  if (Array.isArray(value)) {
    return (
      <ul style={{ paddingLeft: 16, margin: 0 }}>
        {value.map((item, idx) => (
          <li key={idx} style={{ listStyle: 'disc', marginBottom: 2 }}>{item}</li>
        ))}
      </ul>
    );
  }
  if (key.startsWith('url_') && value) {
    return (
      <a href={value} target="_blank" rel="noopener noreferrer">Открыть</a>
    );
  }
  if (typeof value === 'boolean') {
    return value ? 'Да' : '—';
  }
  if (typeof value === 'string' && value.trim() === 'Нет') {
    return '—';
  }
  return value || '—';
}

/** Безопасная строка JSON для отладочного вывода */
export function safeJsonStringify(data, space = 2) {
  try {
    return JSON.stringify(data, null, space);
  } catch {
    return String(data);
  }
}

/** Дата актуализации БД (ISO и т.п.) — строка для UI */
export function formatDbActualDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** wipoDates с сервера — строка, ISO или объект с датами по ключам */
export function formatWipoDatesDisplay(wipo) {
  if (wipo == null || wipo === '') return '—';
  if (typeof wipo === 'string') return formatDbActualDate(wipo) || '—';
  if (typeof wipo === 'object') {
    const parts = Object.entries(wipo).map(([k, v]) => {
      const fv = typeof v === 'string' ? formatDbActualDate(v) : String(v);
      return `${k}: ${fv}`;
    });
    return parts.length ? parts.join(' · ') : '—';
  }
  return String(wipo);
}
